import React, { useState, useRef, useEffect } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import AuthModal from './AuthModal';
import MaintenancePage from './components/MaintenancePage';
import { BACKEND_URL } from './api/client';
import './App.css';

import { CADS } from './constants';
import { useNewsData } from './hooks/useNewsData';
import { useBetting } from './hooks/useBetting';
import { useTheme } from './hooks/useTheme';

import Toast from './components/Toast';
import Header from './components/Header';
import NewsFeed from './components/NewsFeed';
import VideoReels from './components/VideoReels';
import TopBets from './components/TopBets';
import MarketSection from './components/MarketSection';
import BetModal from './components/BetModal';
import BottomNav from './components/BottomNav';
import ProfilePage from './components/ProfilePage';
import RanksPage from './components/RanksPage';
import PositionsPage from './components/PositionsPage';
import WelcomeModal from './components/WelcomeModal';
import CookieBanner from './components/CookieBanner';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';

// ══════════════════════════════════════════════════════════════
// ABOUT PAGE — constants & components
// ══════════════════════════════════════════════════════════════

const CRITERIA = [
  { label: "Evidence Tracing", weight: "25%", color: "#2196f3", icon: "🔍", desc: "Are claims tied to named documents, filings, or on-record sources?" },
  { label: "Source Independence", weight: "20%", color: "#9c27b0", icon: "🔗", desc: "Multiple independent sources? Flags single-source and circular reporting." },
  { label: "Headline Accuracy", weight: "20%", color: "#e91e63", icon: "📰", desc: "Does the headline actually reflect what the article says?" },
  { label: "Claim Confidence", weight: "15%", color: "#ff9800", icon: "⚖️", desc: "Does the language strength match the evidence strength?" },
  { label: "AI Detection", weight: "15%", color: "#00bcd4", icon: "🤖", desc: "Is the article AI-generated or AI-assisted? Rewards disclosure, penalises undisclosed synthetic content." },
  { label: "Correction Record", weight: "5%", color: "#795548", icon: "✏️", desc: "Does the outlet publish corrections when they get things wrong?" },
];

const FAQS = [
  {
    q: "Who scores the articles?",
    a: "Articles are scored automatically using an AI model trained on journalism standards — checking each criteria against the article's full text. No human bias, no editorial judgment."
  },
  {
    q: "When does a market resolve?",
    a: "Each market has a cadence — daily, weekly, or monthly. When the betting period closes, the final scores are calculated and the market resolves. Payouts are distributed automatically."
  },
  {
    q: "What do the prices mean?",
    a: "Prices reflect the crowd's probability estimate. YES at 63¢ means the market thinks there's a 63% chance the article meets that criteria. Buy YES if you think it's higher, NO if you think it's lower."
  },
  {
    q: "Is this real money?",
    a: "PayDirt is currently in beta with virtual currency. Real-money markets are planned once we've validated the scoring engine with real users."
  },
];

function ExampleMarket() {
  const [picked, setPicked] = useState(null);
  const [resolved, setResolved] = useState(false);

  const handlePick = (side) => {
    if (resolved) return;
    setPicked(side);
  };

  const handleResolve = () => setResolved(true);
  const handleReset = () => { setPicked(null); setResolved(false); };

  const won = resolved && picked === "yes";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>

      {/* The article being bet on */}
      <div style={{
        background: "var(--bs-bg, #0d1117)",
        border: "1px solid var(--bs-border, #1f2937)",
        borderRadius: "8px",
        padding: "14px 16px",
      }}>
        <div style={{ fontSize: "10px", color: "var(--bs-muted, #6b7280)", fontFamily: "monospace", letterSpacing: "0.08em", marginBottom: "6px", textTransform: "uppercase" }}>
          Reuters · Published 2 hours ago
        </div>
        <div style={{ color: "var(--bs-ink, #e5e7eb)", fontWeight: 700, fontSize: "14px", lineHeight: "1.4", marginBottom: "8px" }}>
          Fed officials signal rate cut possible by summer, sources say
        </div>
        <div style={{ color: "var(--bs-muted, #6b7280)", fontSize: "12px", lineHeight: "1.5" }}>
          Several Federal Reserve officials have privately indicated that a rate reduction could come as early as June, according to people familiar with the matter who spoke on condition of anonymity...
        </div>
        <div style={{ marginTop: "10px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "10px", background: "var(--bs-border, #1f2937)", color: "var(--bs-body, #9ca3af)", padding: "2px 8px", borderRadius: "4px" }}>US News</span>
          <span style={{ fontSize: "10px", background: "var(--bs-border, #1f2937)", color: "var(--bs-body, #9ca3af)", padding: "2px 8px", borderRadius: "4px" }}>Federal Reserve</span>
        </div>
      </div>

      {/* The market */}
      <div style={{
        background: "#111827",
        border: `1px solid ${resolved ? (won ? "#22c55e" : "#ef4444") : "#22c55e44"}`,
        borderRadius: "10px",
        padding: "18px 20px",
        transition: "border-color 0.3s",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
          <span style={{ background: "#ff980018", color: "#ff9800", fontSize: "9px", fontWeight: 800, borderRadius: "3px", padding: "2px 6px", letterSpacing: "0.08em", textTransform: "uppercase", border: "1px solid #ff980033" }}>
            ⚖️ Claim Confidence
          </span>
          <span style={{ color: "var(--bs-muted, #6b7280)", fontSize: "10px", fontFamily: "monospace" }}>· Closes in 48h</span>
        </div>

        <p style={{ color: "var(--bs-ink, #e5e7eb)", fontWeight: 600, fontSize: "14px", marginBottom: "4px", lineHeight: "1.4" }}>
          Will this Reuters article use unnamed or anonymous sources?
        </p>
        <p style={{ color: "var(--bs-muted, #6b7280)", fontSize: "11px", marginBottom: "14px", lineHeight: "1.5" }}>
          Resolves YES if the article's score shows reliance on anonymous sourcing
        </p>

        {/* Odds bar */}
        <div style={{ marginBottom: "12px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", fontFamily: "monospace", color: "var(--bs-muted, #6b7280)", marginBottom: "4px" }}>
            <span style={{ color: "#22c55e" }}>YES 63%</span>
            <span style={{ color: "#ef4444" }}>NO 37%</span>
          </div>
          <div style={{ height: "4px", background: "var(--bs-border, #1f2937)", borderRadius: "2px", overflow: "hidden" }}>
            <div style={{ height: "100%", width: "63%", background: "linear-gradient(90deg, #22c55e, #16a34a)", borderRadius: "2px" }} />
          </div>
        </div>

        {!resolved ? (
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={() => handlePick("yes")} style={{
              flex: 1,
              background: picked === "yes" ? "#14532d" : "#0a1a0a",
              border: `1px solid ${picked === "yes" ? "#22c55e" : "#166534"}`,
              borderRadius: "6px", padding: "10px", cursor: "pointer", transition: "all 0.15s",
            }}>
              <div style={{ color: "#4ade80", fontWeight: 900, fontSize: "13px" }}>YES</div>
              <div style={{ color: "#22c55e", fontWeight: 700, fontSize: "18px", marginTop: "2px" }}>63¢</div>
              <div style={{ color: "#6b7280", fontSize: "10px", marginTop: "2px" }}>Win $1.59 per $1</div>
            </button>
            <button onClick={() => handlePick("no")} style={{
              flex: 1,
              background: picked === "no" ? "#2d0a0a" : "#1a0a0a",
              border: `1px solid ${picked === "no" ? "#ef4444" : "#7f1d1d"}`,
              borderRadius: "6px", padding: "10px", cursor: "pointer", transition: "all 0.15s",
            }}>
              <div style={{ color: "#f87171", fontWeight: 900, fontSize: "13px" }}>NO</div>
              <div style={{ color: "#ef4444", fontWeight: 700, fontSize: "18px", marginTop: "2px" }}>37¢</div>
              <div style={{ color: "#6b7280", fontSize: "10px", marginTop: "2px" }}>Win $2.70 per $1</div>
            </button>
          </div>
        ) : (
          <div style={{
            background: won ? "#14532d" : "#2d0a0a",
            border: `1px solid ${won ? "#22c55e" : "#ef4444"}`,
            borderRadius: "8px",
            padding: "14px",
            textAlign: "center",
          }}>
            <div style={{ fontSize: "20px", marginBottom: "4px" }}>{won ? "🎉" : "📉"}</div>
            <div style={{ color: won ? "#22c55e" : "#ef4444", fontWeight: 800, fontSize: "14px" }}>
              {won ? "Market resolved YES — you win!" : "Market resolved YES — you lose this one."}
            </div>
            <div style={{ color: "var(--bs-body, #9ca3af)", fontSize: "11px", marginTop: "4px" }}>
              The AI scorer found 3 anonymous sources in the article.
            </div>
          </div>
        )}

        {picked && !resolved && (
          <div style={{ marginTop: "10px" }}>
            <p style={{ color: "#22c55e", fontSize: "11px", textAlign: "center", marginBottom: "8px" }}>
              ✓ You predicted {picked.toUpperCase()} — article resolves in 48 hours
            </p>
            <button onClick={handleResolve} style={{
              width: "100%", background: "var(--bs-border, #1f2937)", border: "1px solid var(--bs-border, #374151)",
              borderRadius: "6px", padding: "8px", cursor: "pointer",
              color: "var(--bs-body, #9ca3af)", fontSize: "11px", fontFamily: "monospace",
            }}>
              ▶ Fast-forward: see how it resolves
            </button>
          </div>
        )}

        {resolved && (
          <button onClick={handleReset} style={{
            marginTop: "10px", width: "100%", background: "none",
            border: "1px solid var(--bs-border, #1f2937)", borderRadius: "6px", padding: "8px",
            cursor: "pointer", color: "var(--bs-muted, #6b7280)", fontSize: "11px",
          }}>
            ↩ Try again
          </button>
        )}
      </div>

      {/* Scoring breakdown — revealed after resolution */}
      {resolved && (
        <div style={{
          background: "var(--bs-bg, #0d1117)",
          border: "1px solid var(--bs-border, #1f2937)",
          borderRadius: "8px",
          padding: "14px 16px",
          animation: "fadeIn 0.4s ease",
        }}>
          <div style={{ fontSize: "10px", color: "var(--bs-muted, #6b7280)", fontFamily: "monospace", letterSpacing: "0.08em", marginBottom: "10px", textTransform: "uppercase" }}>
            PayDirt Score Breakdown — Reuters article
          </div>
          {[
            { label: "Evidence Tracing", score: 72, color: "#2196f3" },
            { label: "Source Independence", score: 48, color: "#9c27b0" },
            { label: "Headline Accuracy", score: 88, color: "#e91e63" },
            { label: "Claim Confidence", score: 61, color: "#ff9800" },
            { label: "AI Detection", score: 95, color: "#00bcd4" },
            { label: "Correction Record", score: 91, color: "#795548" },
          ].map(c => (
            <div key={c.label} style={{ marginBottom: "8px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                <span style={{ fontSize: "11px", color: "var(--bs-body, #9ca3af)" }}>{c.label}</span>
                <span style={{ fontSize: "11px", fontFamily: "monospace", fontWeight: 700, color: c.color }}>{c.score}</span>
              </div>
              <div style={{ height: "3px", background: "var(--bs-border, #1f2937)", borderRadius: "2px", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${c.score}%`, background: c.color, borderRadius: "2px", transition: "width 0.6s ease" }} />
              </div>
            </div>
          ))}
          <div style={{ marginTop: "12px", paddingTop: "10px", borderTop: "1px solid var(--bs-border, #1f2937)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "11px", color: "var(--bs-muted, #6b7280)" }}>Composite PayDirt Score</span>
            <span style={{ fontSize: "16px", fontFamily: "monospace", fontWeight: 900, color: "#ff9800" }}>68</span>
          </div>
        </div>
      )}
    </div>
  );
}

function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      {FAQS.map((item, i) => (
        <div key={i} style={{
          background: "var(--bs-surface, #111827)",
          border: "1px solid var(--bs-border, #1f2937)",
          borderRadius: "8px",
          overflow: "hidden",
          transition: "border-color 0.2s",
          ...(open === i ? { borderColor: "var(--bs-green-bd, #22c55e44)" } : {}),
        }}>
          <button onClick={() => setOpen(open === i ? null : i)} style={{
            width: "100%", background: "none", border: "none", cursor: "pointer",
            padding: "14px 16px", display: "flex", justifyContent: "space-between",
            alignItems: "center", textAlign: "left",
          }}>
            <span style={{ color: "var(--bs-ink, #e5e7eb)", fontSize: "13px", fontWeight: 600 }}>{item.q}</span>
            <span style={{ color: "var(--bs-green, #22c55e)", fontSize: "14px", flexShrink: 0, marginLeft: "8px", transition: "transform 0.2s", transform: open === i ? "rotate(45deg)" : "rotate(0deg)" }}>+</span>
          </button>
          {open === i && (
            <div style={{ padding: "0 16px 14px", color: "var(--bs-body, #9ca3af)", fontSize: "13px", lineHeight: "1.6" }}>
              {item.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AboutPage({ setPage }) {
  return (
    <div style={{ maxWidth: "680px", margin: "0 auto", padding: "24px 20px 80px" }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* ── Hero ── */}
      <div style={{ marginBottom: "56px" }}>
        <div style={{ fontSize: "11px", fontFamily: "monospace", color: "var(--bs-green, #22c55e)", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "14px" }}>
          What is Pay Dirt?
        </div>
        <h1 style={{ color: "var(--bs-ink, #ffffff)", fontWeight: 900, fontSize: "30px", lineHeight: "1.2", marginBottom: "16px" }}>
          Follow the news, and bet on<br />
          <span style={{ color: "var(--bs-green, #22c55e)" }}>journalistic outcomes.</span>
        </h1>
        <p style={{ color: "var(--bs-body, #9ca3af)", fontSize: "15px", lineHeight: "1.7", maxWidth: "520px", marginBottom: "20px" }}>
          PayDirt uses AI to score real news articles against credibility standards — things like whether sources are named, whether claims are backed by evidence, and whether clickbait headlines are used. You predict the outcome before the next articles are scored. Accurate predictions earn real prizes.
        </p>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button onClick={() => setPage("markets")} style={{
            background: "var(--bs-green, #22c55e)", color: "var(--bs-bg, #000)", fontWeight: 800,
            fontSize: "13px", border: "none", borderRadius: "7px",
            padding: "10px 20px", cursor: "pointer",
          }}>See live markets →</button>
          <a href="#how-it-works" style={{
            color: "var(--bs-body, #9ca3af)", fontSize: "13px", fontWeight: 600,
            padding: "10px 0", textDecoration: "none",
            borderBottom: "1px solid var(--bs-border, #374151)",
          }}>How does it work?</a>
        </div>
      </div>

      {/* ── How it works ── */}
      <div id="how-it-works" style={{ marginBottom: "56px" }}>
        <div style={{ fontSize: "11px", fontFamily: "monospace", color: "var(--bs-muted, #6b7280)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "20px" }}>
          How it works
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0px" }}>
          {[
            {
              n: "01",
              title: "An article is published",
              desc: "PayDirt pulls real articles from major outlets — Reuters, AP, BBC, CNN and more — as they go live.",
            },
            {
              n: "02",
              title: "Markets open immediately",
              desc: "Markets are based on outlet and author performance over a time period — daily, weekly, or monthly. You're betting on whether a source like Reuters will score above or below a threshold on a given criteria over that window.",
            },
            {
              n: "03",
              title: "You place your prediction",
              desc: "Bet YES or NO on each market. Prices run from 1¢ to 99¢ and reflect the crowd's probability estimate — YES at 63¢ means a 63% chance the outlet meets that criteria. Buy YES if you think it's more likely, NO if you think it's less.",
            },
            {
              n: "04",
              title: "The AI scores the article",
              desc: "For all articles that we have full text access, our AI scoring engine reads the full article and generates a PayDirt Score across all 6 criteria.",
            },
            {
              n: "05",
              title: "Markets resolve, payouts distributed",
              desc: "If the score confirms your prediction, you win. Simple as that.",
            },
          ].map((s, i, arr) => (
            <div key={s.n} style={{ display: "flex", gap: "16px", position: "relative" }}>
              {/* Connector line */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                <div style={{
                  width: "32px", height: "32px", background: "var(--bs-border, #1f2937)",
                  borderRadius: "50%", display: "flex", alignItems: "center",
                  justifyContent: "center", flexShrink: 0, zIndex: 1,
                }}>
                  <span style={{ color: "var(--bs-green, #22c55e)", fontWeight: 900, fontSize: "10px", fontFamily: "monospace" }}>{s.n}</span>
                </div>
                {i < arr.length - 1 && (
                  <div style={{ width: "1px", flex: 1, background: "var(--bs-border, #1f2937)", minHeight: "24px" }} />
                )}
              </div>
              <div style={{ paddingBottom: i < arr.length - 1 ? "24px" : "0", paddingTop: "6px" }}>
                <div style={{ color: "var(--bs-ink, #ffffff)", fontWeight: 700, fontSize: "14px", marginBottom: "4px" }}>{s.title}</div>
                <div style={{ color: "var(--bs-body, #9ca3af)", fontSize: "13px", lineHeight: "1.6" }}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Scoring criteria ── */}
      <div style={{ marginBottom: "56px" }}>
        <div style={{ fontSize: "11px", fontFamily: "monospace", color: "var(--bs-muted, #6b7280)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "6px" }}>
          What gets scored
        </div>
        <p style={{ color: "var(--bs-body, #9ca3af)", fontSize: "13px", lineHeight: "1.6", marginBottom: "20px" }}>
          Every article is measured on these criteria — these are the same things you're betting on.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {CRITERIA.map(c => (
            <div key={c.label} style={{
              background: "var(--bs-surface, #111827)", border: "1px solid var(--bs-border, #1f2937)",
              borderRadius: "8px", padding: "12px 16px",
              display: "flex", alignItems: "center", gap: "12px",
            }}>
              <span style={{ fontSize: "18px", flexShrink: 0 }}>{c.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px" }}>
                  <span style={{ color: "var(--bs-ink, #ffffff)", fontWeight: 700, fontSize: "13px" }}>{c.label}</span>
                  <span style={{ fontSize: "9px", fontFamily: "monospace", fontWeight: 700, color: c.color, background: c.color + "18", padding: "1px 5px", borderRadius: "3px" }}>{c.weight}</span>
                </div>
                <span style={{ color: "var(--bs-muted, #6b7280)", fontSize: "12px", lineHeight: "1.4" }}>{c.desc}</span>
              </div>
            </div>
          ))}
        </div>
        <p style={{ color: "var(--bs-muted, #6b7280)", fontSize: "11px", marginTop: "10px", fontFamily: "monospace" }}>
          Weights add up to 100% — a composite PayDirt Score is published with every article.
        </p>
      </div>

      {/* ── Live example ── */}
      <div style={{ marginBottom: "56px" }}>
        <div style={{ fontSize: "11px", fontFamily: "monospace", color: "var(--bs-muted, #6b7280)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "6px" }}>
          Try it — live example
        </div>
        <p style={{ color: "var(--bs-body, #9ca3af)", fontSize: "13px", lineHeight: "1.6", marginBottom: "16px" }}>
          Here's a real article and a real market. Pick YES or NO, then fast-forward to see how it resolves.
        </p>
        <ExampleMarket />
      </div>

      {/* ── Why PayDirt exists ── */}
      <div style={{ marginBottom: "56px", borderLeft: "2px solid var(--bs-green, #22c55e)", paddingLeft: "20px" }}>
        <div style={{ fontSize: "11px", fontFamily: "monospace", color: "var(--bs-muted, #6b7280)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "10px" }}>
          Why this exists
        </div>
        <h2 style={{ color: "var(--bs-ink, #ffffff)", fontWeight: 800, fontSize: "17px", marginBottom: "10px", lineHeight: "1.3" }}>
          Trust in news is broken.<br />We're building the fix.
        </h2>
        <p style={{ color: "var(--bs-body, #9ca3af)", fontSize: "13px", lineHeight: "1.7", marginBottom: "10px" }}>
          Right now, there's no shared standard for what makes journalism credible. Outlets self-report corrections. Readers can't easily distinguish hedged language from confident fact. "Sources say" hides a multitude of sins.
        </p>
        <p style={{ color: "var(--bs-body, #9ca3af)", fontSize: "13px", lineHeight: "1.7" }}>
          PayDirt turns that into a public scoreboard. When you bet on journalism standards, you're also helping calibrate them — creating a real-time credibility index that anyone can read.
        </p>
      </div>

      {/* ── FAQ ── */}
      <div style={{ marginBottom: "56px" }}>
        <div style={{ fontSize: "11px", fontFamily: "monospace", color: "var(--bs-muted, #6b7280)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "16px" }}>
          Common questions
        </div>
        <FAQ />
      </div>

      {/* ── CTA ── */}
      <div style={{
        background: "linear-gradient(135deg, #0d1f14, #111827)",
        border: "1px solid var(--bs-green-bd, #22c55e33)",
        borderRadius: "12px",
        padding: "32px 28px",
        textAlign: "center",
      }}>
        <div style={{ fontSize: "22px", marginBottom: "10px" }}>📈</div>
        <h2 style={{ color: "var(--bs-ink, #ffffff)", fontWeight: 900, fontSize: "18px", marginBottom: "8px" }}>
          Ready to start?
        </h2>
        <p style={{ color: "var(--bs-body, #9ca3af)", fontSize: "13px", lineHeight: "1.6", marginBottom: "20px", maxWidth: "340px", margin: "0 auto 20px" }}>
          Live markets are open right now. Pick an outlet or author, bet on how they'll score, and see how they perform over the betting period.
        </p>
        <button onClick={() => setPage("markets")} style={{
          background: "var(--bs-green, #22c55e)", color: "var(--bs-bg, #000)", fontWeight: 800,
          fontSize: "14px", border: "none", borderRadius: "8px",
          padding: "12px 28px", cursor: "pointer", display: "inline-block",
        }}>
          Browse live markets →
        </button>
      </div>

    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// INNER APP — has access to useAuth()
// ══════════════════════════════════════════════════════════════

function PayDirtApp() {
  const { user, showAuth, logout, refreshUser } = useAuth();
  const { theme, toggle } = useTheme();

  const [page, setPage] = useState("home");
  const [hovCat, setHovCat] = useState(null);
  const [hovArticle, setHovArticle] = useState(null);
  const [hovY, setHovY] = useState(0);
  const newsContainerRef = useRef(null);
  const [mTab, setMTab] = useState("sources");
  const [cadTab, setCadTab] = useState("weekly");
  const [xp, setXp] = useState({});
  const [tip, setTip] = useState(null);
  const [toast, setToast] = useState(null);

  // ── Balance: use server balance when logged in, local otherwise ──
  const [localBal, setLocalBal] = useState(1000);
  const bal = user ? user.balance : localBal;
  const setBal = (fn) => {
    if (user) { refreshUser(); } else { setLocalBal(fn); }
  };

  const showToast = (m, t = "success") => { setToast({ m, t }); setTimeout(() => setToast(null), 3000); };
  const toggleTip = id => setTip(p => p === id ? null : id);

  // ── Data hooks ──
  const { sourceMarkets, authorMarkets, videoReels, loading, byCat, fetchAll } = useNewsData();
  const { modal, setModal, betAmt, setBetAmt, myBets, openBet, placeBet, getAllBets } = useBetting({ user, showAuth, refreshUser, bal, setBal, showToast });

  const allBets = getAllBets(sourceMarkets, authorMarkets);
  const actBets = myBets.filter(b => b.status === "active");

  const fT = mTab === "sources" ? "source" : mTab === "authors" ? "author" : "all";
  const fBets = cad => allBets.filter(b => b.cad === cad && (fT === "all" || b.type === fT));
  const curCad = CADS.find(c => c.id === cadTab);
  const curBets = fBets(cadTab);

  // ── Top bets for home page ──
  const topBets = (() => {
    const sorted = [...allBets].sort((a, b) => (b.yV + b.nV) - (a.yV + a.nV));
    const picked = [];
    const entityCount = {};
    const mainLineSeen = new Set();
    for (const b of sorted) {
      if (picked.length >= 8) break;
      const key = b.entity;
      if (b.questionType === 'main_line' && mainLineSeen.has(key)) continue;
      entityCount[key] = (entityCount[key] || 0);
      if (entityCount[key] >= 2) continue;
      if (b.questionType === 'main_line') mainLineSeen.add(key);
      entityCount[key]++;
      picked.push(b);
    }
    return picked;
  })();

  // ── Auth success handler ──
  const handleAuthSuccess = (authUser, type) => {
    if (type === 'register') {
      showToast(`Welcome to PayDirt, ${authUser.username}! 1,000 bonus credits added`);
    } else {
      showToast(`Welcome back, ${authUser.username}`);
    }
  };

  return (
    <div style={{ fontFamily: "'DM Sans',sans-serif", background: "var(--bs-bg, #0a0a0a)", color: "var(--bs-body, #e0e0e0)", minHeight: "100vh", paddingBottom: 80 }} onClick={() => setTip(null)}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Dela+Gothic+One&family=DM+Sans:wght@400;500;700&family=JetBrains+Mono:wght@400;600;700&display=swap');*{box-sizing:border-box;margin:0;padding:0}::-webkit-scrollbar{width:4px}::-webkit-scrollbar-thumb{background:#333;border-radius:2px}@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}@keyframes slideIn{from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}}@keyframes toastIn{from{opacity:0;transform:translateY(-20px)}to{opacity:1;transform:translateY(0)}}@keyframes fadeSlideIn{from{opacity:0;transform:translateY(-2px)}to{opacity:1;transform:translateY(0)}}@keyframes tickerScroll{0%{transform:translateY(0)}100%{transform:translateY(-50%)}}@keyframes arrowSlide{0%{transform:translateY(3px)}50%{transform:translateY(-3px)}100%{transform:translateY(0)}}`}</style>

      <Toast toast={toast} />
      <AuthModal onSuccess={handleAuthSuccess} />
      <WelcomeModal user={user} showAuth={showAuth} setPage={setPage} />
      <Header user={user} bal={bal} page={page} setPage={setPage} showAuth={showAuth} logout={logout} theme={theme} toggle={toggle} />

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 12px", paddingBottom: 40 }}>
        {page === "home" && <>
          {loading && <div style={{ textAlign: "center", padding: "20px 0" }}><span style={{ fontSize: 12, fontFamily: "JetBrains Mono", color: "var(--bs-muted, #555)" }}>Loading...</span></div>}
          <NewsFeed byCat={byCat} hovCat={hovCat} setHovCat={setHovCat} hovArticle={hovArticle} setHovArticle={setHovArticle} hovY={hovY} setHovY={setHovY} newsContainerRef={newsContainerRef} />
          <VideoReels videoReels={videoReels} />
          <TopBets topBets={topBets} allBetsCount={allBets.length} openBet={openBet} setPage={setPage} showToast={showToast} fetchAll={fetchAll} />
        </>}

        {page === "markets" && <>
          <MarketSection mTab={mTab} setMTab={setMTab} cadTab={cadTab} setCadTab={setCadTab} curCad={curCad} curBets={curBets} fBets={fBets} actBets={actBets} xp={xp} setXp={setXp} tip={tip} toggleTip={toggleTip} openBet={openBet} showToast={showToast} fetchAll={fetchAll} />
        </>}
      </div>

      {page === "about" && <AboutPage setPage={setPage} />}
      {page === "profile" && <ProfilePage user={user} showAuth={showAuth} showToast={showToast} />}
      {page === "ranks" && <RanksPage user={user} setPage={setPage} showAuth={showAuth} />}
      {page === "positions" && <PositionsPage user={user} setPage={setPage} showAuth={showAuth} />}
      {page === "terms" && <TermsPage setPage={setPage} />}
      {page === "privacy" && <PrivacyPage setPage={setPage} />}

      <footer style={{ borderTop: "1px solid var(--bs-border, #1a1a1a)", padding: "24px 16px", marginTop: 40, textAlign: "center", fontSize: 11, fontFamily: "JetBrains Mono", color: "var(--bs-muted, #555)" }}>
        <span>&copy; 2026 PayDirt Inc. All rights reserved.</span>
        <div style={{ marginTop: 6, display: "flex", justifyContent: "center", gap: 16 }}>
          <span onClick={() => setPage('terms')} style={{ color: "var(--bs-muted, #666)", textDecoration: "none", cursor: "pointer" }}>Terms of Service</span>
          <span onClick={() => setPage('privacy')} style={{ color: "var(--bs-muted, #666)", textDecoration: "none", cursor: "pointer" }}>Privacy Policy</span>
        </div>
      </footer>

      <CookieBanner />

      <BottomNav page={page} setPage={setPage} showToast={showToast} showAuth={showAuth} user={user} />
      <BetModal modal={modal} setModal={setModal} betAmt={betAmt} setBetAmt={setBetAmt} placeBet={placeBet} user={user} bal={bal} showAuth={showAuth} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ROOT EXPORT — wraps everything in AuthProvider
// ══════════════════════════════════════════════════════════════

export default function App() {
  const [maintenance, setMaintenance] = useState(false);

  useEffect(() => {
    const check = () =>
      fetch(`${BACKEND_URL}/api/maintenance-mode`)
        .then(r => r.json())
        .then(d => setMaintenance(!!d.maintenance))
        .catch(() => {});
    check();
    const interval = setInterval(check, 60000);
    return () => clearInterval(interval);
  }, []);

  if (maintenance) return <MaintenancePage />;

  return (
    <AuthProvider>
      <PayDirtApp />
    </AuthProvider>
  );
}
